import { index, pgTable, timestamp, uniqueIndex, uuid } from 'drizzle-orm/pg-core';
import { applications } from './applications.ts';
import { users } from './users.ts';

/**
 * Tabela `user_favorites` — a estrela do catálogo, POR USUÁRIO.
 *
 * Antes o favorito era a coluna GLOBAL `applications.favorite`: marcar a estrela
 * mudava o card de todos (e, na prática, não persistia — a UI só guardava
 * no estado local). Favorito é preferência pessoal, então virou tabela de
 * associação usuário × aplicação.
 *
 * Modelagem:
 *  - as duas FKs são `onDelete: 'cascade'` — apagar o usuário ou a aplicação
 *    apaga o vínculo (nada de favorito órfão apontando para app inexistente);
 *  - o unique `(user_id, application_id)` é o que dá IDEMPOTÊNCIA ao
 *    `PUT /api/me/favorites/:applicationId` (insert + onConflictDoNothing);
 *  - o índice por `user_id` atende a agregação que calcula o campo `favorite`
 *    do DTO em `GET /api/applications` (uma query com `inArray`, sem N+1).
 *
 * A VISIBILIDADE não é checada aqui: favoritar exige que o usuário possa VER a
 * aplicação, e essa regra é única e vive em `services/access-filter.ts`
 * (`applicationVisibilityFilter`), aplicada na rota antes do insert. Perder o
 * acesso depois de favoritar não apaga a linha — o app simplesmente para de
 * aparecer na lista (que já é filtrada), então o favorito volta a valer se o
 * acesso voltar.
 */
export const userFavorites = pgTable(
  'user_favorites',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    applicationId: uuid('application_id')
      .notNull()
      .references(() => applications.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex('user_favorites_user_app_uq').on(t.userId, t.applicationId),
    index('user_favorites_user_id_idx').on(t.userId),
  ],
);

export type UserFavorite = typeof userFavorites.$inferSelect;
export type NewUserFavorite = typeof userFavorites.$inferInsert;
